import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { StepType } from '../types';
import { STEP_ICONS, STEP_BG_COLORS } from '../constants/stepStyles';
import type { IntegrationProvider } from '@/lib/integrations-constants';
import { getIntegrationMetadata } from '@/lib/integrations-constants';
import { useTranslation } from 'react-i18next';

interface QuickStepSelectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectStepType: (type: StepType) => void;
  storeType?: IntegrationProvider;
  connectedProviders?: IntegrationProvider[];
}

interface StepTemplate {
  type: StepType;
  label: string;
  description: string;
  group: 'pages' | 'storefronts' | 'other';
}

const STEP_TEMPLATES: StepTemplate[] = [
  { type: 'landing', label: 'quickStep.landingPage', description: 'quickStep.landingPageDesc', group: 'pages' },
  { type: 'checkout', label: 'quickStep.checkout', description: 'quickStep.checkoutDesc', group: 'pages' },
  { type: 'offer', label: 'quickStep.offerUpsell', description: 'quickStep.offerUpsellDesc', group: 'pages' },
  { type: 'thankyou', label: 'quickStep.thankYou', description: 'quickStep.thankYouDesc', group: 'pages' },
  { type: 'custom', label: 'quickStep.customPage', description: 'quickStep.customPageDesc', group: 'pages' },
  { type: 'storefront_shopify', label: 'quickStep.shopifyStorefront', description: 'quickStep.shopifyStorefrontDesc', group: 'storefronts' },
  { type: 'storefront_woocommerce', label: 'quickStep.wooCommerceStorefront', description: 'quickStep.wooCommerceStorefrontDesc', group: 'storefronts' },
  { type: 'external', label: 'quickStep.externalPage', description: 'quickStep.externalPageDesc', group: 'other' },
];

export function QuickStepSelectionDialog({
  open,
  onOpenChange,
  onSelectStepType,
  storeType,
  connectedProviders = [],
}: QuickStepSelectionDialogProps) {
  const { t } = useTranslation('storefront');
  function handleSelectType(type: StepType) {
    onSelectStepType(type);
    onOpenChange(false);
  }

  const hasShopify = storeType === 'shopify' || connectedProviders.includes('shopify');
  const hasWooCommerce = storeType === 'woocommerce' || connectedProviders.includes('woocommerce');
  const hasAnyStorefront = hasShopify || hasWooCommerce;

  const filteredTemplates = STEP_TEMPLATES.filter((t) => {
    if (t.type === 'storefront_shopify') return hasShopify;
    if (t.type === 'storefront_woocommerce') return hasWooCommerce;
    return true;
  });

  const pageSteps = filteredTemplates.filter(t => t.group === 'pages');
  const storefrontSteps = filteredTemplates.filter(t => t.group === 'storefronts');
  const otherSteps = filteredTemplates.filter(t => t.group === 'other');

  const renderItem = (template: StepTemplate) => {
    const Icon = STEP_ICONS[template.type];
    const bgColor = STEP_BG_COLORS[template.type] || 'bg-slate-500';

    let logo: string | null = null;
    if (template.type === 'storefront_shopify') logo = getIntegrationMetadata('shopify')?.logoUrl ?? null;
    if (template.type === 'storefront_woocommerce') logo = getIntegrationMetadata('woocommerce')?.logoUrl ?? null;

    return (
      <button
        key={template.type}
        onClick={() => handleSelectType(template.type)}
        className="hover:border-primary/60 hover:bg-primary/5 flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-left transition-all dark:border-slate-700 dark:bg-slate-900"
      >
        <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${bgColor}`}>
          {logo ? (
            <img src={logo} alt="" className="h-4 w-4 object-contain" />
          ) : (
            <Icon className="h-4 w-4 text-white" />
          )}
        </div>
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-900 dark:text-slate-100">{t(template.label)}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400">{t(template.description)}</div>
        </div>
      </button>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('quickStep.title')}</DialogTitle>
          <DialogDescription className="sr-only">{t('quickStep.dialogDescription')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-1 pb-2">
          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wider text-slate-400 dark:text-slate-500">{t('quickStep.pages')}</p>
            <div className="grid grid-cols-2 gap-2">
              {pageSteps.map(renderItem)}
            </div>
          </div>

          {hasAnyStorefront && storefrontSteps.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wider text-slate-400 dark:text-slate-500">{t('quickStep.storefronts')}</p>
              <div className="grid grid-cols-2 gap-2">
                {storefrontSteps.map(renderItem)}
              </div>
            </div>
          )}

          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wider text-slate-400 dark:text-slate-500">{t('quickStep.other')}</p>
            <div className="grid grid-cols-2 gap-2">
              {otherSteps.map(renderItem)}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
